import { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useActivePopupAd } from '@/hooks/useAdsManager';
import { useIsMobile } from '@/hooks/use-mobile';

const POPUP_STORAGE_KEY = 'popup_ad_shown';

function getPopupStorageKey(popupId: string, frequency: string): string {
  if (frequency === 'day') {
    const today = new Date().toISOString().split('T')[0];
    return `${POPUP_STORAGE_KEY}_${popupId}_${today}`;
  }
  return `${POPUP_STORAGE_KEY}_${popupId}_session`;
}

function hasSeenPopup(popupId: string, frequency: string): boolean {
  const key = getPopupStorageKey(popupId, frequency);
  
  if (frequency === 'session') {
    return sessionStorage.getItem(key) === 'true';
  }
  return localStorage.getItem(key) === 'true';
}

function markPopupAsSeen(popupId: string, frequency: string): void {
  const key = getPopupStorageKey(popupId, frequency);
  
  if (frequency === 'session') {
    sessionStorage.setItem(key, 'true');
  } else {
    localStorage.setItem(key, 'true');
  }
}

export function PopupAdDisplay() {
  const { data: popup } = useActivePopupAd();
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();
  const contentRef = useRef<HTMLDivElement>(null);
  const hasTriggered = useRef(false);

  // Check device targeting
  const matchesDevice = (() => {
    if (!popup?.device_targeting || popup.device_targeting === 'all') return true;
    if (popup.device_targeting === 'mobile' && isMobile) return true;
    if (popup.device_targeting === 'desktop' && !isMobile) return true;
    return false;
  })();

  // Check if popup has valid content
  const hasContent = (() => {
    if (!popup) return false;
    if (popup.content_type === 'image') {
      return popup.image_url && popup.image_url.trim().length > 0;
    }
    return popup.ad_code && popup.ad_code.trim().length > 0;
  })();

  // Should we show the popup at all?
  const shouldShow = popup?.is_enabled && matchesDevice && hasContent;

  // Handle popup triggers
  useEffect(() => {
    if (!shouldShow || !popup || hasTriggered.current) return;
    
    const frequency = popup.frequency || 'session';
    if (hasSeenPopup(popup.id, frequency)) return;

    const triggerType = popup.trigger_type || 'delay';
    const triggerValue = popup.trigger_value || 5;

    if (triggerType === 'delay') {
      const timer = setTimeout(() => {
        setIsVisible(true);
        hasTriggered.current = true;
      }, triggerValue * 1000);
      return () => clearTimeout(timer);
    }

    if (triggerType === 'scroll') {
      const handleScroll = () => {
        const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrollPercent >= triggerValue && !hasTriggered.current) {
          setIsVisible(true);
          hasTriggered.current = true;
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }

    if (triggerType === 'exit' && !isMobile) {
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 0 && !hasTriggered.current) {
          setIsVisible(true);
          hasTriggered.current = true;
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
      return () => document.removeEventListener('mouseleave', handleMouseLeave);
    }
  }, [shouldShow, popup, isMobile]);

  // Inject HTML/JS content
  useEffect(() => {
    if (!isVisible || !contentRef.current || !popup) return;
    
    if (popup.content_type === 'code' && popup.ad_code) {
      contentRef.current.innerHTML = popup.ad_code;
      
      // Execute scripts
      const scripts = contentRef.current.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode?.replaceChild(newScript, oldScript);
      });
    }
  }, [isVisible, popup]);

  const handleClose = () => {
    if (popup) {
      markPopupAsSeen(popup.id, popup.frequency || 'session');
    }
    setIsVisible(false);
  };

  if (!isVisible || !popup) return null;

  const isModal = popup.popup_type === 'modal';
  const isSlide = popup.popup_type === 'slide';

  // Modal popup
  if (isModal) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />
        
        {/* Content */}
        <div className="relative bg-background rounded-lg shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-auto">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 z-10 p-2 rounded-full bg-background/80 hover:bg-background border border-border shadow-sm transition-colors"
            aria-label="Close popup"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="p-4">
            {popup.content_type === 'image' && popup.image_url ? (
              popup.link_url ? (
                <a 
                  href={popup.link_url}
                  target={popup.open_in_new_tab ? '_blank' : '_self'}
                  rel={popup.open_in_new_tab ? 'noopener noreferrer' : undefined}
                  className="block"
                >
                  <img src={popup.image_url} alt="Advertisement" className="w-full h-auto" />
                </a>
              ) : (
                <img src={popup.image_url} alt="Advertisement" className="w-full h-auto" />
              )
            ) : (
              <div ref={contentRef} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Slide-in popup (bottom)
  if (isSlide) {
    return (
      <div 
        className={cn(
          "fixed bottom-0 left-0 right-0 z-[9999] bg-background border-t border-border shadow-2xl",
          "transform transition-transform duration-300 ease-out",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
      >
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 z-10 p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
          aria-label="Close popup"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-4 max-w-4xl mx-auto">
          {popup.content_type === 'image' && popup.image_url ? (
            popup.link_url ? (
              <a 
                href={popup.link_url}
                target={popup.open_in_new_tab ? '_blank' : '_self'}
                rel={popup.open_in_new_tab ? 'noopener noreferrer' : undefined}
                className="block"
              >
                <img src={popup.image_url} alt="Advertisement" className="w-full h-auto max-h-[200px] object-contain" />
              </a>
            ) : (
              <img src={popup.image_url} alt="Advertisement" className="w-full h-auto max-h-[200px] object-contain" />
            )
          ) : (
            <div ref={contentRef} />
          )}
        </div>
      </div>
    );
  }

  return null;
}
